import React from 'react';
import { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'underscore';
import processApi from '@/apis/processApi';
import { i18next } from '@/config/i18n';
import { SetOptimizationData } from '@/actions/common/commonPure';
import { setPlanningStatusCode } from '@/actions/plannings/pure';
import { useToast } from './ToastContext';

interface ProcessSummary {
  number_of_routes: number;
  number_of_customer_stops: number;
  number_of_planned_shipments: number;
  number_of_unplanned_shipments: number;
  number_of_planned_vehicles: number;
  total_distance_m: number;
  total_working_time_min: number;
}

interface ProcessData {
  summary: ProcessSummary;
}

interface RootState {
  core: {
    selected_process: {
      optimization_id: string;
    };
  };
}

export const useDashboardData = () => {
  const dispatch = useDispatch();
  const { success, info, error } = useToast();

  const { selected_process } = useSelector(
    (state: RootState) => state.core,
    isEqual
  );
  const optimizationId = selected_process?.optimization_id;

  const [process, setProcess] = useState<ProcessData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const statusController = useRef<AbortController | null>(null);
  const dataController = useRef<AbortController | null>(null);

  const formatTime = (totalMinutes: number): string => {
    if (!totalMinutes) return '--';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} h ${minutes} min`;
  };

  const convertMeterToKm = (meters: number): number => Math.round(meters / 1000);

  // handle optimization status response
  const handleStatus = async (state: string, description?: string): Promise<void> => {
    switch (state) {
      case 'FAILURE':
        error(
          <>
            <b>{i18next.t('common:states.errorTitle')}:</b>&nbsp;
            {i18next.t('common:states.optimizationFailed')}
          </>
        );
        dispatch(setPlanningStatusCode(500));
        throw new Error('FAILURE');

      case 'ACCEPTED':
      case 'RUNNING':
        info(
          <>
            <b>{i18next.t('common:states.runningTitle')}:</b>&nbsp;
            {i18next.t('common:states.optimizationRunning')}
          </>,
          { autoHideMs: 2500 }
        );
        dispatch(setPlanningStatusCode(102));
        throw new Error('RUNNING');

      case 'SUCCESS':
        dispatch(setPlanningStatusCode(200));
        return;

      default:
        error(
          <>
            <b>{i18next.t('common:states.errorTitle')}:</b>&nbsp;
            {description || i18next.t('common:states.unknownError')}
          </>
        );
        dispatch(setPlanningStatusCode(500));
        throw new Error('UNKNOWN_STATE');
    }
  };

  // handle unexpected errors
  const handleError = (err: any) => {
    const known = ['FAILURE', 'RUNNING'];
    if (known.includes(err.message)) return;

    const status = err?.response?.status;
    const message =
      status === 500
        ? i18next.t('common:states.server')
        : status === 404
          ? i18next.t('common:states.IdNotFound')
          : i18next.t('common:states.unknownError');

    error(
      <>
        <b>{i18next.t('common:states.errorTitle')}:</b>&nbsp;{message}
      </>,
      { autoHideMs: 5000 }
    );

    setProcess(null);
    dispatch(SetOptimizationData(null));
    dispatch(setPlanningStatusCode(status ?? 500));
  };

  const fetchDashboardData = useCallback(async () => {
    if (!optimizationId) return;

    statusController.current?.abort();
    dataController.current?.abort();

    const statusCtrl = new AbortController();
    const dataCtrl = new AbortController();
    statusController.current = statusCtrl;
    dataController.current = dataCtrl;

    setLoading(true);
    try {
      const { data: statusRes } = await processApi.getOptimizationStatus(
        optimizationId,
        { signal: statusCtrl.signal }
      );
      await handleStatus(statusRes.state, statusRes.description);

      const { data: processData } = await processApi.getProcessById(
        optimizationId,
        { signal: dataCtrl.signal }
      );
      setProcess(processData);
      dispatch(SetOptimizationData(processData));
      success(
        <>
          <b>{i18next.t('common:states.successTitle')}:</b>&nbsp;
          {i18next.t('common:states.optimizationSuccess')}
        </>
      );
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [optimizationId, dispatch]);

  useEffect(() => {
    fetchDashboardData();
    return () => {
      statusController.current?.abort();
      dataController.current?.abort();
    };
  }, [optimizationId, fetchDashboardData]);

  const numTours = process?.summary.number_of_routes ?? 0;
  const numStops = process?.summary.number_of_customer_stops ?? 0;
  const numPlanned = process?.summary.number_of_planned_shipments ?? 0;
  const numDropped = process?.summary.number_of_unplanned_shipments ?? 0;
  const usedVehicles = process?.summary.number_of_planned_vehicles ?? 0;
  const totalDistance = convertMeterToKm(process?.summary.total_distance_m ?? 0);
  const totalWorkingTime = formatTime(process?.summary.total_working_time_min ?? 0);

  return {
    optimizationId,
    usedVehicles,
    totalDistance,
    totalWorkingTime,
    loading,
    numTours,
    numPlanned,
    numDropped,
    numStops,
  };
};